"use client";

import React from "react";
import { ComponentSchema } from "@/lib/ecoBuilder/schema/types";
import { cn } from "@/lib/utils";
import { RichTextRenderer } from '@/components/ecoBuilder/atoms/RichTextRenderer';
import { PaddingHandle } from "@/components/ecoBuilder/editor/PaddingHandle";

// ═══════════════════════════════════════════════════════════════
// Schema Definition
// ═══════════════════════════════════════════════════════════════
export const ImageSectionSchema: ComponentSchema = {
    // Content
    image: {
        type: "image",
        label: "الصورة",
        description: "اختر صورة من المعرض أو ارفع صورة جديدة",
    },
    title: {
        type: "text",
        label: "العنوان الرئيسي",
        defaultValue: "عنوان القسم",
        placeholder: "اكتب العنوان هنا...",
    },
    subtitle: {
        type: "textarea",
        label: "العنوان الفرعي",
        defaultValue: "اضف وصفاً قصيراً للصورة أو القسم هنا.",
        placeholder: "اكتب الوصف...",
    },
    // Appearance
    height: {
        type: "select",
        label: "ارتفاع القسم",
        defaultValue: "auto",
        options: [
            { value: "auto", label: "تلقائي (حسب المحتوى)" },
            { value: "small", label: "صغير (300px)" },
            { value: "medium", label: "متوسط (500px)" },
            { value: "large", label: "كبير (700px)" },
            { value: "screen", label: "ملء الشاشة" },
        ],
    },
    overlayOpacity: {
        type: "slider",
        label: "شفافية الطبقة المعتمة",
        defaultValue: 0,
        sliderConfig: { min: 0, max: 90, step: 10, unit: "%" },
    },
    textColor: {
        type: "color",
        label: "لون النصوص",
        defaultValue: "#000000",
    },
    // Layout
    paddingTop: {
        type: "slider",
        label: "مسافة علوية",
        defaultValue: 60,
        sliderConfig: { min: 0, max: 200, step: 4, unit: "px" },
    },
    paddingBottom: {
        type: "slider",
        label: "مسافة سفلية",
        defaultValue: 60,
        sliderConfig: { min: 0, max: 200, step: 4, unit: "px" },
    },
};

export const ImageSectionDefaultProps = {
    title: "عنوان القسم المميز",
    subtitle: "هذا النص هو مثال لنص يمكن أن يستبدل في نفس المساحة.",
    height: "auto",
    overlayOpacity: 0,
    textColor: "#000000",
    paddingTop: 60,
    paddingBottom: 60,
};

// ═══════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════
export function ImageSection({
    image,
    title,
    subtitle,
    height = "auto",
    overlayOpacity = 0,
    textColor = "#000000",
    paddingTop = 60,
    paddingBottom = 60,
    isSelected,
    onUpdateProps,
}: any) {

    // Height Calculation
    const getHeightClass = () => {
        switch (height) {
            case "screen": return "min-h-screen";
            case "large": return "min-h-[700px]";
            case "medium": return "min-h-[500px]";
            case "small": return "min-h-[300px]";
            default: return "min-h-fit"; // Auto
        }
    };

    const handlePaddingChange = (side: 'top' | 'bottom') => (newVal: number) => {
        if (!onUpdateProps) return;
        if (side === 'top') onUpdateProps({ paddingTop: newVal });
        else onUpdateProps({ paddingBottom: newVal });
    };

    return (
        <section
            className={cn(
                "relative group w-full overflow-hidden flex flex-col justify-center items-center text-center transition-all duration-300",
                getHeightClass(),
                // Visual feedback for selection
                isSelected ? "" : ""
            )}
            style={{
                paddingTop: `${paddingTop}px`,
                paddingBottom: `${paddingBottom}px`,
                color: textColor,
            }}
        >
            {/* Background Image */}
            {image && (
                <div
                    className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat transition-transform duration-700 group-hover:scale-105"
                    style={{ backgroundImage: `url(${image})` }}
                />
            )}

            {/* Overlay */}
            <div
                className="absolute inset-0 z-10 bg-black transition-opacity duration-300"
                style={{ opacity: overlayOpacity / 100 }}
            />

            {/* Content Container */}
            <div className="relative z-20 container mx-auto px-4 max-w-4xl">
                {title && (
                    <RichTextRenderer
                        html={title}
                        className="text-3xl md:text-5xl font-bold mb-4 leading-tight"
                    />
                )}
                {subtitle && (
                    <RichTextRenderer
                        html={subtitle}
                        className="text-lg md:text-xl opacity-90 leading-relaxed max-w-2xl mx-auto"
                    />
                )}

                {/* Fallback if no image */}
                {!image && (
                    <div className="mt-8 border-2 border-dashed border-current/20 rounded-xl p-8 bg-current/5">
                        <span className="opacity-50">اختر صورة من الخصائص لعرضها هنا</span>
                    </div>
                )}
            </div>

            {/* Padding Handles (Only when selected) */}
            {isSelected && onUpdateProps && (
                <>
                    <PaddingHandle
                        side="top"
                        currentPadding={paddingTop}
                        onPaddingChange={handlePaddingChange('top')}
                    />
                    <PaddingHandle
                        side="bottom"
                        currentPadding={paddingBottom}
                        onPaddingChange={handlePaddingChange('bottom')}
                    />
                </>
            )}
        </section>
    );
}
